import { useMutation, useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../core/network/api';
import { standardKeys as keys } from '../queries/keys';
import { standardPaths as paths } from '../queries/paths';

const read = (path, signal) => api.get(path, { signal }).then(r => r.data);
const readingOptions = { staleTime: 60_000, refetchOnWindowFocus: false };

function unconfirmed(message) {
    return Object.assign(new Error(message), { requiresRefresh: true });
}

export function useCatalog() {
    return useQuery({ ...readingOptions, queryKey: keys.catalog, queryFn: async ({ signal }) => {
        const items = [];
        for (let offset = 0; ;) {
            const { data } = await api.get(paths.list(), { signal, params: { limit: 100, offset } });
            const page = data.items ?? [];
            items.push(...page); offset += page.length;
            if (!page.length || offset >= data.pagination?.total || (!data.pagination && page.length < 100)) return items;
        }
    } });
}
export function useStandard(id) {
    return useQuery({ ...readingOptions, queryKey: keys.detail(id), enabled: !!id, queryFn: ({ signal }) => read(paths.detail(id), signal) });
}
export function useVersions(id) {
    return useQuery({ ...readingOptions, queryKey: keys.versions(id), enabled: !!id, queryFn: ({ signal }) => read(paths.versions(id), signal).then(d => d.items ?? []) });
}
export function useVersion(id) {
    return useQuery({ ...readingOptions, queryKey: keys.version(id), enabled: !!id, queryFn: ({ signal }) => read(paths.version(id), signal) });
}
export function useRules(id) {
    const names = ['sections', 'members', 'resolved'];
    const queries = useQueries({ queries: names.map(name => ({
        ...readingOptions, queryKey: keys[name](id), enabled: !!id,
        queryFn: ({ signal }) => read(paths[name](id), signal).then(data => data.items ?? []),
    })) });
    return {
        data: Object.fromEntries(names.map((name, i) => [name, queries[i].data ?? []])),
        isPending: queries.some(query => query.isPending),
        isSuccess: queries.every(query => query.isSuccess),
        error: queries.find(query => query.error)?.error,
        refetch: () => Promise.all(queries.map(query => query.refetch())),
    };
}

// A fresh state check avoids editing a version published since the form was opened.
// This is a UI safeguard; the API remains responsible for concurrent writes.
async function requireDraft(id, qc) {
    const version = await read(paths.version(id));
    qc.setQueryData(keys.version(id), version);
    if (version.version_state !== 'draft') throw unconfirmed('Esta versión ya está publicada. Recarga y crea un borrador nuevo.');
}

export function useStandardAction() {
    const qc = useQueryClient();
    return useMutation({ retry: false, mutationFn: async ({ action, id, versionId, payload }) => {
        if (['editVersion', 'createSection', 'editSection', 'addMember', 'removeMember'].includes(action)) await requireDraft(versionId, qc);
        switch (action) {
            case 'createStandard': {
                const { data } = await api.post(paths.list(), payload);
                return read(paths.detail(data.id));
            }
            case 'editStandard':
                await api.patch(paths.detail(id), payload);
                return read(paths.detail(id));
            case 'createVersion': {
                const { data } = await api.post(paths.versions(id), payload);
                return read(paths.version(data.id));
            }
            case 'editVersion':
                await api.patch(paths.version(versionId), payload);
                return read(paths.version(versionId));
            case 'createSection': {
                // The API upserts by key: never silently overwrite an existing section.
                const current = await read(paths.sections(versionId));
                qc.setQueryData(keys.sections(versionId), current.items);
                if (current.items.some(item => item.key === payload.key)) throw new Error('Esa clave ya existe. Edita la sección existente.');
                const { data } = await api.post(paths.sections(versionId), payload);
                return data;
            }
            case 'editSection': {
                const { data } = await api.patch(paths.section(id), payload);
                return data;
            }
            case 'addMember': {
                const candidate = await read(paths.version(payload.member_version_id));
                if (candidate.version_state !== 'published') throw new Error('El miembro debe ser una versión publicada.');
                await api.post(paths.members(versionId), payload);
                const saved = await read(paths.members(versionId));
                if (!saved.items.some(member => member.version_id === payload.member_version_id)) throw unconfirmed('El servidor no confirma el miembro elegido. Recarga el conjunto.');
                return saved;
            }
            case 'removeMember': {
                await api.delete(paths.member(versionId, id));
                const saved = await read(paths.members(versionId));
                if (saved.items.some(member => member.version_id === id)) throw unconfirmed('El servidor todavía muestra el miembro. Recarga el conjunto.');
                return saved;
            }
            case 'publish': {
                const rules = await read(paths.resolved(versionId));
                const members = await read(paths.members(versionId));
                if (!rules.items?.length) throw new Error('Agrega secciones o miembros con reglas antes de publicar.');
                if (members.items.some(item => item.version_state !== 'published')) throw new Error('Todos los miembros deben estar publicados.');
                await api.post(paths.publish(versionId));
                const version = await read(paths.version(versionId));
                if (version.version_state !== 'published') throw unconfirmed('La lectura del servidor no confirma la publicación. Recarga antes de reintentar.');
                return version;
            }
            default: throw new Error('Operación de estándares desconocida.');
        }
    }, onSuccess: async (data, { action, id, versionId }) => {
        const invalidate = queryKey => qc.invalidateQueries({ queryKey });
        if (['createSection', 'editSection'].includes(action)) {
            await qc.cancelQueries({ queryKey: keys.sections(versionId) });
            qc.setQueryData(keys.sections(versionId), (rows = []) => {
                const next = rows.filter(row => row.id !== data.id).concat(data);
                return next.sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
            });
            // Only resolved rules changed; membership and catalog did not.
            await invalidate(keys.resolved(versionId));
        } else if (['addMember', 'removeMember'].includes(action)) {
            qc.setQueryData(keys.members(versionId), data.items);
            await invalidate(keys.resolved(versionId));
        } else if (['createStandard', 'editStandard'].includes(action)) {
            qc.setQueryData(keys.detail(data.id), data);
            await Promise.all([invalidate(keys.catalog), invalidate(['repository-standards'])]);
        } else {
            qc.setQueryData(keys.version(data.id), data);
            const affected = [invalidate(keys.versions(data.standard_id ?? id))];
            if (action === 'publish') affected.push(invalidate(['repository-standards']));
            await Promise.all(affected);
        }
    } });
}
